import { getTotalBrutoFaturas } from '@/services/fatura'
import type { Fatura } from '@/types/fatura'
import { formatDate, formatMoney } from '@/utils'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AsaasBadge from './AsaasBadge'
import FaturaValorResumo from './FaturaValorResumo'

interface DetalhesPagamentoPagoProps {
    faturas: Fatura[]
}

function resolveComprovanteUrl(path?: string | null) {
    if (!path) return null
    if (/^https?:\/\//i.test(path)) return path
    const base = (import.meta.env.VITE_PROOF_OF_PAYMENT_BASE_URL as string | undefined) ?? ''
    return `${base}${path}`
}

export default function DetalhesPagamentoPago({ faturas }: DetalhesPagamentoPagoProps) {
    if (faturas.length === 0) return null

    const faturaPrincipal = faturas[0]
    const pagamento = faturaPrincipal.pagamento
    const forma = pagamento?.Forma ?? faturaPrincipal.FormaPagamento ?? '—'
    const dataPagamento = pagamento?.Data ?? faturaPrincipal.DataPagamento
    const valorPagamento = pagamento?.Valor != null
        ? Number(pagamento.Valor)
        : getTotalBrutoFaturas(faturas)
    const desconto = Number(pagamento?.Desconto ?? 0)
    const acrescimo = Number(pagamento?.Acrescimo ?? 0)
    const parcelas = Number(pagamento?.Parcelas ?? 1)
    const comprovante = resolveComprovanteUrl(
        pagamento?.Comprovante ?? faturas.find(f => f.Comprovante)?.Comprovante,
    )
    const boletoLink = faturas.find(f => f.boleto?.Link)?.boleto?.Link

    return (
        <div className="pagamento-panel">
            <div className="pagamento-panel-header">
                <h4 className="pagamento-panel-title">Detalhes do pagamento</h4>
            </div>

            <FaturaValorResumo faturas={faturas} variant="panel" className="pagamento-panel-valores" />

            <div className="pagamento-form-stack">
                <div className="pagamento-deposito-grid">
                    <div className="pagamento-deposito-field">
                        <label className="form-label">Forma de pagamento</label>
                        <input
                            type="text"
                            className="form-control deposito-field-input"
                            disabled
                            readOnly
                            value={forma}
                        />
                    </div>

                    <div className="pagamento-deposito-field">
                        <label className="form-label">Data do pagamento</label>
                        <input
                            type="text"
                            className="form-control deposito-field-input"
                            disabled
                            readOnly
                            value={dataPagamento ? formatDate(dataPagamento) : '—'}
                        />
                    </div>

                    <div className="pagamento-deposito-field">
                        <label className="form-label">Valor pago</label>
                        <input
                            type="text"
                            className="form-control deposito-field-input"
                            disabled
                            readOnly
                            value={formatMoney(valorPagamento)}
                        />
                    </div>

                    {pagamento?.CdPagamento ? (
                        <div className="pagamento-deposito-field">
                            <label className="form-label">Código do pagamento</label>
                            <input
                                type="text"
                                className="form-control deposito-field-input"
                                disabled
                                readOnly
                                value={`#${pagamento.CdPagamento}`}
                            />
                        </div>
                    ) : null}

                    {parcelas > 1 ? (
                        <div className="pagamento-deposito-field">
                            <label className="form-label">Parcelas</label>
                            <input
                                type="text"
                                className="form-control deposito-field-input"
                                disabled
                                readOnly
                                value={`${parcelas}x`}
                            />
                        </div>
                    ) : null}

                    {desconto > 0 ? (
                        <div className="pagamento-deposito-field">
                            <label className="form-label">Desconto</label>
                            <input
                                type="text"
                                className="form-control deposito-field-input"
                                disabled
                                readOnly
                                value={`- ${formatMoney(desconto)}`}
                            />
                        </div>
                    ) : null}

                    {acrescimo > 0 ? (
                        <div className="pagamento-deposito-field">
                            <label className="form-label">Acréscimo</label>
                            <input
                                type="text"
                                className="form-control deposito-field-input"
                                disabled
                                readOnly
                                value={`+ ${formatMoney(acrescimo)}`}
                            />
                        </div>
                    ) : null}

                    {pagamento?.Cartao ? (
                        <div className="pagamento-deposito-field">
                            <label className="form-label">Cartão</label>
                            <input
                                type="text"
                                className="form-control deposito-field-input"
                                disabled
                                readOnly
                                value={
                                    pagamento.Titular
                                        ? `${pagamento.Cartao} — ${pagamento.Titular}`
                                        : pagamento.Cartao
                                }
                            />
                        </div>
                    ) : null}
                </div>

                {(comprovante || boletoLink) && (
                    <div className="pagamento-actions" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {comprovante && (
                            <a
                                className="btn btn-outline-primary"
                                href={comprovante}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" /> Ver comprovante
                            </a>
                        )}
                        {boletoLink && (
                            <a
                                className="btn btn-outline-primary"
                                href={boletoLink}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" /> Ver boleto
                            </a>
                        )}
                    </div>
                )}
            </div>

            <AsaasBadge />
        </div>
    )
}
